/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Draft, PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type {
  OnErrorWorkspacePresetActionPayload,
  OnSuccesWorkspacePresetActionPayload,
  OpenWorkspaceActionDialogPayload,
  SearchWorkspaceFilter,
  SetWorkspaceLabelFiltersPayload,
  SubmitFormWorkspaceActionDialogPayload,
  ToggleListDialogPayload,
  ToggleSelectFilterChip,
  WorkspaceListFilter,
  WorkspaceListState,
} from './types';
import { PresetFilterType } from './types';

export const personalFilterState = {
  label: 'shared-filter-my-presets',
  id: PresetFilterType.personal,
  isSelected: true,
  isDisabled: false,
};
export const systemFilterState = {
  label: 'shared-filter-system-presets',
  id: PresetFilterType.system,
  isSelected: true,
  isDisabled: false,
};
export const sharedFilterState = {
  label: 'shared-filter-shared-presets',
  id: PresetFilterType.shared,
  isSelected: true,
  isDisabled: false,
};

export const pluginFilterState = {
  label: 'shared-filter-plugin-view-presets',
  id: PresetFilterType.plugin,
  isSelected: true,
  isDisabled: false,
};

export const defaultPresetFilterState: WorkspaceListFilter[] = [
  personalFilterState,
  systemFilterState,
  sharedFilterState,
];

export const initialState: WorkspaceListState = {
  error: undefined,
  isWorkspaceListDialogOpen: false,
  searchQuery: '',
  workspaceListFilters: defaultPresetFilterState,
  workspaceLabelFilters: undefined,
};

const slice = createSlice({
  initialState,
  name: 'workspaceList',
  reducers: {
    openWorkspaceActionDialogOptions: (
      draft: Draft<WorkspaceListState>,
      action: PayloadAction<OpenWorkspaceActionDialogPayload>,
    ) => {
      draft.workspaceActionDialog = {
        ...action.payload,
        error: undefined,
      };
    },
    onSuccessWorkspaceSaveDialog: (
      draft: Draft<WorkspaceListState>,
      action: PayloadAction<{ presetId: string }>,
    ) => {
      if (!draft.workspaceActionDialog) {
        return;
      }
      const { presetId } = action.payload;
      if (presetId) {
        draft.workspaceActionDialog.presetId = presetId;
      }
      draft.workspaceActionDialog!.hasSaved = true;
    },
    submitFormWorkspaceActionDialogOptions: (
      draft: Draft<WorkspaceListState>,
      _action: PayloadAction<SubmitFormWorkspaceActionDialogPayload>,
    ) => {
      if (!draft.workspaceActionDialog) {
        return;
      }
      draft.workspaceActionDialog!.error = undefined;
    },
    closeWorkspaceActionDialogOptions: (draft: Draft<WorkspaceListState>) => {
      delete draft.workspaceActionDialog;
    },
    // action is intercepted in mapPresets/listener
    onSuccessWorkspacePresetAction: (
      draft: Draft<WorkspaceListState>,
      _action: PayloadAction<OnSuccesWorkspacePresetActionPayload>,
    ) => {
      delete draft.workspaceActionDialog;
    },
    onErrorWorkspacePresetAction: (
      draft: Draft<WorkspaceListState>,
      action: PayloadAction<OnErrorWorkspacePresetActionPayload>,
    ) => {
      const { error } = action.payload;

      if (draft.workspaceActionDialog) {
        draft.workspaceActionDialog.error = error;
      } else {
        // fallback if dialog isn't open (e.g. SHARE action)
        draft.error = error;
      }
    },

    toggleWorkspaceDialog: (
      draft: Draft<WorkspaceListState>,
      action: PayloadAction<ToggleListDialogPayload>,
    ) => {
      const { isWorkspaceListDialogOpen: isListDialogOpen } = action.payload;
      draft.isWorkspaceListDialogOpen = isListDialogOpen;
    },
    toggleSelectFilterChip: (
      draft: Draft<WorkspaceListState>,
      action: PayloadAction<ToggleSelectFilterChip>,
    ) => {
      const { id, isSelected, isAdmin } = action.payload;

      const relevantFilters = isAdmin
        ? draft.workspaceListFilters!.filter(
            (filter) => filter.id === 'system' || filter.id === 'shared',
          )
        : draft.workspaceListFilters!;

      const index = draft.workspaceListFilters!.findIndex(
        (filter) => filter.id === id,
      );

      if (index !== -1) {
        if (isAdmin) {
          draft.workspaceListFilters!.forEach((filter, i) => {
            draft.workspaceListFilters![i].isSelected = filter.id === id;
          });
        } else {
          const isAllSelected = relevantFilters.every(
            (filter) => filter.isSelected,
          );

          if (isAllSelected) {
            draft.workspaceListFilters!.forEach((filter, i) => {
              draft.workspaceListFilters![i].isSelected =
                filter.id === id ? isSelected : false;
            });
          } else {
            draft.workspaceListFilters![index] = {
              ...draft.workspaceListFilters![index],
              isSelected,
            };

            const isAllUnSelected = relevantFilters.every(
              (filter) => !filter.isSelected,
            );
            if (isAllUnSelected) {
              draft.workspaceListFilters!.forEach((_, i) => {
                draft.workspaceListFilters![i].isSelected = true;
              });
            }
          }
        }
      }
    },

    setSelectAllFilterChip: (draft: Draft<WorkspaceListState>) => {
      draft.workspaceListFilters = draft.workspaceListFilters!.map((filter) => {
        return { ...filter, isSelected: true };
      });
    },
    setFilterChips: (
      draft: Draft<WorkspaceListState>,
      action: PayloadAction<ToggleSelectFilterChip[]>,
    ) => {
      const { payload } = action;

      draft.workspaceListFilters!.forEach(
        (filter: WorkspaceListFilter, index) => {
          const existingFilter = payload.find(
            (existingFilter) => existingFilter.id === filter.id,
          );
          const updatedFilter = {
            ...filter,
            isSelected: existingFilter
              ? existingFilter.isSelected
              : filter.isSelected,
          };
          draft.workspaceListFilters![index] = updatedFilter;
        },
      );
    },
    setWorkspaceLabelFilters: (
      draft: Draft<WorkspaceListState>,
      action: PayloadAction<SetWorkspaceLabelFiltersPayload>,
    ) => {
      draft.workspaceLabelFilters = action.payload.labels;
    },
    clearLabelFilters: (draft: Draft<WorkspaceListState>) => {
      draft.workspaceLabelFilters = undefined;
    },
    searchFilter: (
      draft: Draft<WorkspaceListState>,
      action: PayloadAction<SearchWorkspaceFilter>,
    ) => {
      const { searchQuery } = action.payload;
      draft.searchQuery = searchQuery;
    },
  },
});

export const { reducer: workspaceListReducer, actions: workspaceListActions } =
  slice;
